import { useCallback, useId, useMemo, useRef, useState, type MouseEvent } from "react";
import type { EthBpsSparklineFrame } from "../hooks/useEthBpsSparkline";

/** Match EthBpsChart TX/RX colors. */
export const ETH_BPS_SPARKLINE_SERIES = {
  tx: { label: "TX", stroke: "#1f6feb", fillTop: 0.35, fillBottom: 0 },
  rx: { label: "RX", stroke: "#cf222e", fillTop: 0.32, fillBottom: 0 },
} as const;

const SERIES = ETH_BPS_SPARKLINE_SERIES;
const LINE_WIDTH = 1.25;
const WINDOW_MS = 30_000;
const WIDTH = 260;
const HEIGHT = 40;

type SeriesKey = keyof typeof SERIES;

type HoverInfo = {
  xPx: number;
  yPx: number;
  /** 0 at top … 1 at bottom of the sparkline. */
  fracFromTop: number;
};

function buildStepPaths(
  frames: EthBpsSparklineFrame[],
  key: SeriesKey,
  windowStart: number,
  minY: number,
  yRange: number,
) {
  if (!frames.length) return null;

  const toX = (ts: number) => ((ts - windowStart) / WINDOW_MS) * WIDTH;
  const toY = (v: number) => HEIGHT - ((v - minY) / yRange) * HEIGHT;
  const value = (f: EthBpsSparklineFrame) => (key === "tx" ? f.tx_Mbps : f.rx_Mbps);

  const first = frames[0]!;
  let line = `M${toX(first.ts_ms).toFixed(2)},${toY(value(first)).toFixed(2)}`;
  for (let i = 1; i < frames.length; i++) {
    const f = frames[i]!;
    line += `H${toX(f.ts_ms).toFixed(2)}V${toY(value(f)).toFixed(2)}`;
  }

  const area = [line, `V${HEIGHT}`, `H${toX(first.ts_ms).toFixed(2)}`, "Z"].join(" ");
  return { line, area };
}

function buildChart(frames: EthBpsSparklineFrame[]) {
  if (!frames.length) return null;

  const windowEnd = frames.reduce((max, f) => Math.max(max, f.ts_ms), 0);
  const windowStart = windowEnd - WINDOW_MS;
  const inWindow = frames.filter((f) => f.ts_ms >= windowStart);
  if (!inWindow.length) return null;

  const vals = inWindow.flatMap((f) => [f.tx_Mbps, f.rx_Mbps]);
  let minY = 0;
  let maxY = Math.max(...vals, 100);
  const span = maxY - minY;
  const pad = span > 0 ? span * 0.12 : 0.5;
  maxY += pad;
  const yRange = maxY - minY || 1;

  return {
    minY,
    yRange,
    tx: buildStepPaths(inWindow, "tx", windowStart, minY, yRange),
    rx: buildStepPaths(inWindow, "rx", windowStart, minY, yRange),
  };
}

export function EthBpsSparkline({ frames }: { frames: EthBpsSparklineFrame[] }) {
  const gradTx = useId();
  const gradRx = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const chart = useMemo(() => buildChart(frames), [frames]);

  const hoverMbps =
    hover && chart ? chart.minY + chart.yRange * (1 - hover.fracFromTop) : null;

  const onMove = useCallback((ev: MouseEvent<HTMLDivElement>) => {
    if (!wrapRef.current) {
      setHover(null);
      return;
    }
    const rect = wrapRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xPx = Math.min(Math.max(ev.clientX - rect.left, 0), rect.width);
    const yPx = Math.min(Math.max(ev.clientY - rect.top, 0), rect.height);
    setHover({ xPx, yPx, fracFromTop: yPx / rect.height });
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative w-full"
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="block h-10 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Ethernet TX/RX sparkline"
      >
        <defs>
          <linearGradient id={gradRx} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES.rx.stroke} stopOpacity={SERIES.rx.fillTop} />
            <stop offset="100%" stopColor={SERIES.rx.stroke} stopOpacity={SERIES.rx.fillBottom} />
          </linearGradient>
          <linearGradient id={gradTx} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES.tx.stroke} stopOpacity={SERIES.tx.fillTop} />
            <stop offset="100%" stopColor={SERIES.tx.stroke} stopOpacity={SERIES.tx.fillBottom} />
          </linearGradient>
        </defs>
        {chart?.rx && (
          <>
            <path d={chart.rx.area} fill={`url(#${gradRx})`} stroke="none" />
            <path
              d={chart.rx.line}
              fill="none"
              stroke={SERIES.rx.stroke}
              strokeWidth={LINE_WIDTH}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
        {chart?.tx && (
          <>
            <path d={chart.tx.area} fill={`url(#${gradTx})`} stroke="none" />
            <path
              d={chart.tx.line}
              fill="none"
              stroke={SERIES.tx.stroke}
              strokeWidth={LINE_WIDTH}
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>
      {hover && hoverMbps != null && (
        <>
          <div
            className="pointer-events-none absolute inset-x-0 h-px bg-text-tertiary/50"
            style={{ top: hover.yPx }}
          />
          <div
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded border border-border-subtle bg-card px-1.5 py-0.5 font-medium tabular-nums text-text-primary shadow-sm"
            style={{
              fontSize: "10px",
              left: Math.min(hover.xPx + 6, (wrapRef.current?.clientWidth ?? 200) - 72),
              top: Math.max(hover.yPx - 18, 0),
            }}
          >
            {hoverMbps.toFixed(1)} Mbit/s
          </div>
        </>
      )}
    </div>
  );
}
